import { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHoverIntent } from '@/hooks/use-hover-intent';
import { useModal } from '@/hooks/use-modal';
import {
    buildCellPositionMap,
    cloneTableBlock,
    getCellAt,
    getContrastTextColor,
    getHeaderLabels,
    getHeaderRowCount,
    normalizeColumnWidths,
    normalizeTableBlock,
} from '@/lib/table-utils';
import { cn } from '@/lib/utils';
import { Alignments } from '@/types/report';
import { TableBlock, TableCell, TableCellColor } from '@/types/report-payload';
import { TestStatisticsModal, TestStatisticsModalHandle } from '../test-table/TestStatisticsModal';
import { findMetricId } from '../test-table/utils';
import { DragIndicators } from './DragIndicators';
import {
    canDeleteHeaderRowAt,
    canInsertHeaderRowAt,
    insertColumn,
    insertRow,
} from './TableActions';
import { TableContextMenu } from './TableContextMenu';
import { TableEdgePill } from './TableEdgePill';
import { TableRenderer } from './TableRenderer';
import { CellPosition, getSelectionRectGlobal, globalRowToPosition } from './TableSelection';
import { useColumnDrag } from './useColumnDrag';
import { useColumnResize } from './useColumnResize';
import { useRowDrag } from './useRowDrag';
import { useTableActions } from './useTableActions';
import { useTableKeyboardShortcuts } from './useTableKeyboardShortcuts';
import { useTablePaste } from './useTablePaste';
import { useTableSelection } from './useTableSelection';
import type { DragEvent as ReactDragEvent, MouseEvent as ReactMouseEvent } from 'react';

type TableHoverTarget =
    | { kind: 'row'; index: number; element: HTMLTableRowElement }
    | { kind: 'column'; index: number; element: HTMLTableCellElement };

interface TestTableEditorProps {
    block: TableBlock;
    onUpdate: (updates: Partial<TableBlock>) => void;
    primaryColor?: string;
    headerColor?: string;
    textAlignment?: Alignments;
    /** Colore a coluna "Classificação" pelo rótulo. Ausente = ligado. */
    colorClassifications?: boolean;
    disabled?: boolean;
    onAddChart?: () => void;
    measurementId?: string;
}

export default function TestTableEditor({
    block,
    onUpdate,
    primaryColor,
    colorClassifications = true,
    headerColor,
    textAlignment = 'center',
    disabled = false,
    onAddChart,
    measurementId,
}: TestTableEditorProps) {
    const normalizedBlock = useMemo(() => normalizeTableBlock(block), [block]);
    const { openModal, closeModal } = useModal();
    const [isSelecting, setIsSelecting] = useState(false);
    const [editingCell, setEditingCell] = useState<CellPosition | null>(null);
    const [editSeed, setEditSeed] = useState<{
        cellId: string;
        key: number;
        value: string;
    } | null>(null);
    const [pendingSingleClickEdit, setPendingSingleClickEdit] = useState<CellPosition | null>(null);
    const [editCursorPosition, setEditCursorPosition] = useState<number | null>(null);
    const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
    const [hoverTarget, setHoverTarget] = useState<TableHoverTarget | null>(null);
    const [pillDrag, setPillDrag] = useState<'row' | 'column' | null>(null);
    // The table's own chrome (the edge pills) waits for hover
    // intent, so a cursor crossing the page no longer lights every table it
    // passes. `hoverTarget` still tracks the row/column under the pointer with
    // no delay, so once the chrome is up, moving cell to cell repositions the
    // pill instantly. `keepOpen` holds it through a pill drag — unmounting the
    // HTML5 drag source cancels the drag.
    const tableHover = useHoverIntent({ disabled, keepOpen: pillDrag !== null });
    // Synchronous mirror of pillDrag: guards mouseleave/scroll handlers that can
    // fire before the dragstart state update commits (would unmount the drag source).
    const pillDragRef = useRef<'row' | 'column' | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const tableInnerRef = useRef<HTMLDivElement>(null);
    const tableRef = useRef<HTMLTableElement>(null);
    const tableWrapperRef = useRef<HTMLDivElement>(null);
    const draftRef = useRef<string | null>(null);
    const selectionDragAnchorRef = useRef<CellPosition | null>(null);

    const columnWidths = useMemo(() => normalizeColumnWidths(normalizedBlock), [normalizedBlock]);

    const headerLabels = useMemo(
        () => getHeaderLabels(normalizedBlock.header),
        [normalizedBlock.header],
    );

    // -1 quando a tabela não tem coluna de classificação ou o laudo desligou o
    // ajuste, que é o que o renderer espera. Com duas colunas reconhecíveis
    // (ex.: "Classificação" e "Interpretação") vale a primeira.
    const classificationColIndex = useMemo(
        () =>
            colorClassifications
                ? headerLabels.findIndex((label) => findMetricId(label || '') === 'classification')
                : -1,
        [colorClassifications, headerLabels],
    );

    const getRowLabel = useCallback(
        (rowIndex: number) => {
            const row = normalizedBlock.rows[rowIndex];
            if (!row) return `Linha ${rowIndex + 1}`;
            const value = row.cells.find((cell) => cell.value?.trim())?.value?.trim();
            return value || `Linha ${rowIndex + 1}`;
        },
        [normalizedBlock.rows],
    );

    const cellIdToPosition = useMemo(
        () => buildCellPositionMap(normalizedBlock),
        [normalizedBlock],
    );

    const getCaretOffsetFromPoint = useCallback(
        (clientX: number, clientY: number): number | undefined => {
            // Firefox
            if ('caretPositionFromPoint' in document) {
                const pos = (
                    document as unknown as {
                        caretPositionFromPoint: (x: number, y: number) => { offset: number } | null;
                    }
                ).caretPositionFromPoint(clientX, clientY);
                if (pos) return pos.offset;
            }
            // Chrome, Safari, Edge
            if (document.caretRangeFromPoint) {
                const range = document.caretRangeFromPoint(clientX, clientY);
                if (range) return range.startOffset;
            }
            return undefined;
        },
        [],
    );

    const areSamePosition = useCallback((a: CellPosition | null, b: CellPosition | null) => {
        if (!a || !b) return false;
        return a.section === b.section && a.rowIndex === b.rowIndex && a.colIndex === b.colIndex;
    }, []);

    const startEditingCell = useCallback(
        (position: CellPosition, initialValue?: string, cursorPosition?: number) => {
            if (disabled) return;

            const cell = getCellAt(
                normalizedBlock,
                position.section,
                position.rowIndex,
                position.colIndex,
            );

            if (initialValue !== undefined && cell) {
                setEditSeed({
                    cellId: cell.id,
                    key: Date.now(),
                    value: initialValue,
                });
            } else {
                setEditSeed(null);
            }

            setEditCursorPosition(cursorPosition ?? null);
            setEditingCell(position);
        },
        [disabled, normalizedBlock],
    );

    const columnWidthsRef = useRef(columnWidths);
    useEffect(() => {
        columnWidthsRef.current = columnWidths;
    }, [columnWidths]);

    useEffect(() => {
        const needsHeader = normalizedBlock.header !== block.header;
        const needsBands = normalizedBlock.header_bands !== block.header_bands;
        const needsRows = normalizedBlock.rows !== block.rows;
        const needsWidths = normalizedBlock.columnWidths.length !== columnWidths.length;
        if (needsHeader || needsBands || needsRows || needsWidths) {
            onUpdate({
                header_bands: normalizedBlock.header_bands,
                header: normalizedBlock.header,
                rows: normalizedBlock.rows,
                columnWidths,
            });
        }
    }, [block, columnWidths, normalizedBlock, onUpdate]);

    // Uma fonte só por render: os hooks e o renderer recebem esta contagem em
    // vez de re-derivarem cada um a sua.
    const headerRowCount = getHeaderRowCount(normalizedBlock);

    const headerBackground = headerColor || primaryColor || '#e6e7e8';
    const headerTextColor = getContrastTextColor(headerBackground);

    const getOriginPosition = useCallback(
        (cellId: string): CellPosition | null => {
            return cellIdToPosition.get(cellId) || null;
        },
        [cellIdToPosition],
    );

    const {
        selection,
        setSelection,
        applySelection,
        selectionRect,
        hasMultiCellSelection,
        selectionHasMerge,
    } = useTableSelection({
        block: normalizedBlock,
        getOriginPosition,
    });

    const updateBlock = useCallback(
        (next: TableBlock) => {
            onUpdate({
                header_bands: next.header_bands,
                header: next.header,
                rows: next.rows,
                columnWidths: next.columnWidths,
            });
        },
        [onUpdate],
    );

    const updateCellValue = useCallback(
        (position: CellPosition, value: string) => {
            const next = cloneTableBlock(normalizedBlock, columnWidths);

            const cell = getCellAt(next, position.section, position.rowIndex, position.colIndex);
            if (!cell) return;

            if (cell.mergedInto) {
                const originPos = getOriginPosition(cell.mergedInto);
                if (originPos) {
                    const originCell = getCellAt(
                        next,
                        originPos.section,
                        originPos.rowIndex,
                        originPos.colIndex,
                    );
                    if (originCell) {
                        originCell.value = value;
                    }
                }
            } else {
                cell.value = value;
            }

            updateBlock(next);
        },
        [columnWidths, getOriginPosition, normalizedBlock, updateBlock],
    );

    const commitCurrentEdit = useCallback(() => {
        if (editingCell && draftRef.current !== null) {
            updateCellValue(editingCell, draftRef.current);
        }
        draftRef.current = null;
    }, [editingCell, updateCellValue]);

    const clearSelectedCells = useCallback(() => {
        if (!selection || disabled) return;
        const next = cloneTableBlock(normalizedBlock, columnWidths);

        const globalRect = getSelectionRectGlobal(selection, headerRowCount);
        const cleared = new Set<string>();

        for (let globalRow = globalRect.rowStart; globalRow <= globalRect.rowEnd; globalRow++) {
            const { section, rowIndex } = globalRowToPosition(globalRow, headerRowCount);
            for (let col = globalRect.colStart; col <= globalRect.colEnd; col++) {
                const cell = getCellAt(next, section, rowIndex, col);
                if (!cell) continue;

                if (cell.mergedInto) {
                    if (cleared.has(cell.mergedInto)) continue;
                    const originPos = getOriginPosition(cell.mergedInto);
                    if (originPos) {
                        const originCell = getCellAt(
                            next,
                            originPos.section,
                            originPos.rowIndex,
                            originPos.colIndex,
                        );
                        if (originCell) {
                            originCell.value = '';
                            cleared.add(cell.mergedInto);
                        }
                    }
                } else {
                    cell.value = '';
                    cleared.add(cell.id);
                }
            }
        }

        updateBlock(next);
    }, [columnWidths, disabled, getOriginPosition, normalizedBlock, selection, updateBlock]);

    const copySelection = useCallback(() => {
        if (!selection) return;
        const globalRect = getSelectionRectGlobal(selection, headerRowCount);
        const rows: string[] = [];

        for (let globalRow = globalRect.rowStart; globalRow <= globalRect.rowEnd; globalRow++) {
            const { section, rowIndex } = globalRowToPosition(globalRow, headerRowCount);
            const cols: string[] = [];
            for (let col = globalRect.colStart; col <= globalRect.colEnd; col++) {
                const cell = getCellAt(normalizedBlock, section, rowIndex, col);
                if (!cell) {
                    cols.push('');
                    continue;
                }
                if (cell.mergedInto) {
                    const originPos = getOriginPosition(cell.mergedInto);
                    if (originPos) {
                        const originCell = getCellAt(
                            normalizedBlock,
                            originPos.section,
                            originPos.rowIndex,
                            originPos.colIndex,
                        );
                        // Only put the value in the top-left position of the merged cell
                        const isTopLeft =
                            section === originPos.section &&
                            rowIndex === originPos.rowIndex &&
                            col === originPos.colIndex;
                        cols.push(isTopLeft ? originCell?.value || '' : '');
                    } else {
                        cols.push('');
                    }
                } else {
                    cols.push(cell.value || '');
                }
            }
            rows.push(cols.join('\t'));
        }

        const tsv = rows.join('\n');
        navigator.clipboard.writeText(tsv).catch(() => {
            // Clipboard write can fail if the document is not focused
        });
    }, [getOriginPosition, normalizedBlock, selection]);

    const selectAll = useCallback(() => {
        if (disabled) return;
        const maxCol = Math.max(0, normalizedBlock.header.cells.length - 1);
        const maxRow = Math.max(0, normalizedBlock.rows.length - 1);
        applySelection({
            start: { section: 'header', rowIndex: 0, colIndex: 0 },
            end: { section: 'body', rowIndex: maxRow, colIndex: maxCol },
        });
    }, [applySelection, disabled, normalizedBlock]);

    const handleDraftChange = useCallback((value: string) => {
        draftRef.current = value;
    }, []);

    /** Re-focus the outer [data-table-editor] wrapper so keyboard/paste listeners stay active. */
    const focusWrapper = useCallback(() => {
        const outerDiv = tableWrapperRef.current?.closest(
            '[data-table-editor]',
        ) as HTMLElement | null;
        outerDiv?.focus({ preventScroll: true });
    }, [tableWrapperRef]);

    const handleCellMouseDown = useCallback(
        (position: CellPosition, event: ReactMouseEvent) => {
            if (disabled) return;
            if (event.button === 2 || (event.button === 0 && event.ctrlKey)) return;

            // Resolve merged-cell target BEFORE preventDefault so we can
            // detect clicks inside the currently-editing cell.
            const cell = getCellAt(
                normalizedBlock,
                position.section,
                position.rowIndex,
                position.colIndex,
            );
            let targetPosition = position;
            if (cell?.mergedInto) {
                const originPos = getOriginPosition(cell.mergedInto);
                if (originPos) targetPosition = originPos;
            }

            // If the user clicks inside the cell that is already being edited,
            // let the browser handle cursor repositioning natively — no
            // preventDefault, no commit, no selection change.
            if (editingCell && areSamePosition(targetPosition, editingCell)) {
                return;
            }

            if (event.button === 0) {
                event.preventDefault();
            }

            const isSingleSelection =
                !!selection &&
                selection.start.section === selection.end.section &&
                selection.start.rowIndex === selection.end.rowIndex &&
                selection.start.colIndex === selection.end.colIndex;
            const isAlreadySelected =
                isSingleSelection && areSamePosition(selection?.start || null, targetPosition);

            if (!event.shiftKey && isAlreadySelected) {
                setPendingSingleClickEdit(targetPosition);
            } else {
                setPendingSingleClickEdit(null);
            }

            const dragAnchor = event.shiftKey && selection ? selection.start : targetPosition;

            selectionDragAnchorRef.current = dragAnchor;

            applySelection({ start: dragAnchor, end: targetPosition });

            commitCurrentEdit();
            setEditingCell(null);
            setEditSeed(null);
            setIsSelecting(true);

            // event.preventDefault() (above) blocks the browser from naturally
            // focusing the clicked element. Explicitly focus the outer
            // [data-table-editor] div so paste/keyboard listeners can verify
            // that focus is inside this table editor.
            focusWrapper();
        },
        [
            applySelection,
            areSamePosition,
            commitCurrentEdit,
            disabled,
            editingCell,
            focusWrapper,
            getOriginPosition,
            normalizedBlock,
            selection,
        ],
    );

    const handleCellMouseEnter = useCallback(
        (position: CellPosition, event: ReactMouseEvent) => {
            if (!disabled && !pillDragRef.current) {
                const cellElement = event.currentTarget as HTMLTableCellElement;
                setHoverTarget((prev) => {
                    if (position.section === 'header') {
                        // Só a folha vira alvo de coluna: numa faixa o colIndex
                        // é o da primeira coluna do grupo, e a pílula ofereceria
                        // inserir no lugar errado.
                        if (position.rowIndex !== headerRowCount - 1) return prev;
                        return prev?.kind === 'column' &&
                            prev.index === position.colIndex &&
                            prev.element === cellElement
                            ? prev
                            : { kind: 'column', index: position.colIndex, element: cellElement };
                    }
                    const rowElement = cellElement.closest('tr');
                    if (!rowElement) return prev;
                    return prev?.kind === 'row' &&
                        prev.index === position.rowIndex &&
                        prev.element === rowElement
                        ? prev
                        : { kind: 'row', index: position.rowIndex, element: rowElement };
                });
            }

            if (!isSelecting || !selection) return;
            setPendingSingleClickEdit(null);
            const dragAnchor = selectionDragAnchorRef.current ?? selection.start;

            applySelection({ start: dragAnchor, end: position });
        },
        [applySelection, disabled, isSelecting, selection],
    );

    const handleCellClick = useCallback(
        (position: CellPosition, event: ReactMouseEvent) => {
            if (disabled) return;
            if (event.shiftKey) return;
            if (!pendingSingleClickEdit) return;

            if (areSamePosition(pendingSingleClickEdit, position)) {
                const cursorOffset = getCaretOffsetFromPoint(event.clientX, event.clientY);
                startEditingCell(position, undefined, cursorOffset);
            }

            setPendingSingleClickEdit(null);
        },
        [
            areSamePosition,
            disabled,
            getCaretOffsetFromPoint,
            pendingSingleClickEdit,
            startEditingCell,
        ],
    );

    const handleCellContextMenu = useCallback(
        (position: CellPosition, _event: ReactMouseEvent) => {
            if (disabled) return;
            if (hasMultiCellSelection) return;
            const cell = getCellAt(
                normalizedBlock,
                position.section,
                position.rowIndex,
                position.colIndex,
            );
            let targetPosition = position;
            if (cell?.mergedInto) {
                const originPos = getOriginPosition(cell.mergedInto);
                if (originPos) targetPosition = originPos;
            }
            applySelection({ start: targetPosition, end: targetPosition });
        },
        [applySelection, disabled, getOriginPosition, hasMultiCellSelection, normalizedBlock],
    );

    const handleMouseUp = () => {
        selectionDragAnchorRef.current = null;
        setIsSelecting(false);
    };

    const handleSelectRow = useCallback(
        (rowIndex: number) => {
            if (disabled) return;
            const maxCol = Math.max(0, normalizedBlock.header.cells.length - 1);
            applySelection({
                start: { section: 'body', rowIndex, colIndex: 0 },
                end: { section: 'body', rowIndex, colIndex: maxCol },
            });
            commitCurrentEdit();
            setEditingCell(null);
            setEditSeed(null);
        },
        [applySelection, commitCurrentEdit, disabled, normalizedBlock],
    );

    const handleSelectColumn = useCallback(
        (colIndex: number) => {
            if (disabled) return;
            const maxRow = Math.max(0, normalizedBlock.rows.length - 1);
            applySelection({
                start: { section: 'header', rowIndex: 0, colIndex },
                end: { section: 'body', rowIndex: maxRow, colIndex },
            });
            commitCurrentEdit();
            setEditingCell(null);
            setEditSeed(null);
        },
        [applySelection, commitCurrentEdit, disabled, normalizedBlock],
    );

    const handleStartEdit = (position: CellPosition, event?: ReactMouseEvent) => {
        const cursorOffset = event
            ? getCaretOffsetFromPoint(event.clientX, event.clientY)
            : undefined;
        startEditingCell(position, undefined, cursorOffset);
    };

    const handleCommit = (position: CellPosition, value: string, reason?: 'enter' | 'blur') => {
        draftRef.current = null;
        updateCellValue(position, value);
        setEditingCell(null);
        setEditSeed(null);
        setEditCursorPosition(null);

        if (reason === 'enter' && position.section === 'body') {
            const maxRow = Math.max(0, normalizedBlock.rows.length - 1);
            const nextRow = position.rowIndex + 1;
            if (nextRow <= maxRow) {
                moveSelection(nextRow, position.colIndex);
            }
        }

        // Restore focus to the wrapper so keyboard/paste keeps working.
        // Skip the 'blur' case — the user clicked away intentionally.
        if (reason !== 'blur') {
            focusWrapper();
        }
    };

    const handleCancel = () => {
        draftRef.current = null;
        setEditingCell(null);
        setEditSeed(null);
        setEditCursorPosition(null);
        focusWrapper();
    };

    const {
        handleMerge,
        handleUnmerge,
        handleInsertRowAbove,
        handleInsertRowBelow,
        handleDeleteRows,
        handleInsertColumnLeft,
        handleInsertColumnRight,
        handleDeleteColumns,
        handleSetColor,
    } = useTableActions({
        block: normalizedBlock,
        columnWidths,
        selection,
        selectionRect,
        cellIdToPosition,
        getOriginPosition,
        updateBlock,
        setSelection,
    });

    const { handleResizeStart } = useColumnResize({
        disabled,
        columnWidthsRef,
        onUpdateWidths: (nextWidths) => onUpdate({ columnWidths: nextWidths }),
    });

    const {
        dragOverIndex: _dragOverIndex,
        dragInsertPosition: _dragInsertPosition,
        dragInsertLeft,
        dragInsertTransitionMs,
        dragGhostLabel,
        handleColumnDragStart,
        handleColumnDragEnd,
    } = useColumnDrag({
        disabled,
        headerLabels,
        block: normalizedBlock,
        tableRef,
        updateBlock,
    });

    const {
        rowInsertPosition: _rowInsertPosition,
        rowInsertTop,
        rowInsertTransitionMs,
        rowGhostLabel,
        handleRowDragStart,
        handleRowDragEnd,
    } = useRowDrag({
        disabled,
        block: normalizedBlock,
        tableRef,
        getRowLabel,
        updateBlock,
    });

    const handleRowPillDragStart = useCallback(
        (event: ReactDragEvent) => {
            if (hoverTarget?.kind !== 'row') return;
            pillDragRef.current = 'row';
            setPillDrag('row');
            handleRowDragStart(hoverTarget.index, event);
        },
        [handleRowDragStart, hoverTarget],
    );

    const handleColumnPillDragStart = useCallback(
        (event: ReactDragEvent) => {
            if (hoverTarget?.kind !== 'column') return;
            pillDragRef.current = 'column';
            setPillDrag('column');
            handleColumnDragStart(hoverTarget.index, event);
        },
        [handleColumnDragStart, hoverTarget],
    );

    const clearPillDrag = useCallback(() => {
        pillDragRef.current = null;
        setPillDrag(null);
        setHoverTarget(null);
    }, []);

    const handleRowPillDragEnd = useCallback(() => {
        handleRowDragEnd();
        clearPillDrag();
    }, [clearPillDrag, handleRowDragEnd]);

    const handleColumnPillDragEnd = useCallback(() => {
        handleColumnDragEnd();
        clearPillDrag();
    }, [clearPillDrag, handleColumnDragEnd]);

    const handlePillAddRow = useCallback(() => {
        if (disabled || hoverTarget?.kind !== 'row') return;
        updateBlock(insertRow(normalizedBlock, 'body', hoverTarget.index + 1, cellIdToPosition));
    }, [cellIdToPosition, disabled, hoverTarget, normalizedBlock, updateBlock]);

    const handlePillAddColumn = useCallback(() => {
        if (disabled || hoverTarget?.kind !== 'column') return;
        updateBlock(insertColumn(normalizedBlock, hoverTarget.index + 1, cellIdToPosition));
    }, [cellIdToPosition, disabled, hoverTarget, normalizedBlock, updateBlock]);

    const moveSelection = useCallback(
        (rowIndex: number, colIndex: number) => {
            if (!selection) return false;
            const section = selection.start.section;
            const nextCell = getCellAt(normalizedBlock, section, rowIndex, colIndex);
            if (nextCell?.mergedInto) {
                const originPos = getOriginPosition(nextCell.mergedInto);
                if (originPos) {
                    applySelection({ start: originPos, end: originPos });
                    return true;
                }
            }
            applySelection({
                start: { section, rowIndex, colIndex },
                end: { section, rowIndex, colIndex },
            });
            return true;
        },
        [applySelection, getOriginPosition, normalizedBlock, selection],
    );

    const handleArrowMove = useCallback(
        (direction: 'left' | 'right' | 'up' | 'down', extend = false) => {
            if (!selection) return false;
            const section = selection.start.section;
            const maxRow =
                section === 'header'
                    ? headerRowCount - 1
                    : Math.max(0, normalizedBlock.rows.length - 1);
            const maxCol = Math.max(0, normalizedBlock.header.cells.length - 1);

            if (extend) {
                // Shift+Arrow: keep the anchor (start), move the extent (end)
                const currentRow = selection.end.rowIndex;
                const currentCol = selection.end.colIndex;

                let nextRow = currentRow;
                let nextCol = currentCol;

                if (direction === 'left') nextCol = Math.max(0, currentCol - 1);
                if (direction === 'right') nextCol = Math.min(maxCol, currentCol + 1);
                if (direction === 'up') nextRow = Math.max(0, currentRow - 1);
                if (direction === 'down') nextRow = Math.min(maxRow, currentRow + 1);

                if (nextRow === currentRow && nextCol === currentCol) return false;
                applySelection({
                    start: selection.start,
                    end: { section, rowIndex: nextRow, colIndex: nextCol },
                });
                return true;
            }

            const currentRow = selection.start.rowIndex;
            const currentCol = selection.start.colIndex;

            let nextRow = currentRow;
            let nextCol = currentCol;

            if (direction === 'left') nextCol = Math.max(0, currentCol - 1);
            if (direction === 'right') nextCol = Math.min(maxCol, currentCol + 1);
            if (direction === 'up') nextRow = Math.max(0, currentRow - 1);
            if (direction === 'down') nextRow = Math.min(maxRow, currentRow + 1);

            if (nextRow === currentRow && nextCol === currentCol) return false;
            return moveSelection(nextRow, nextCol);
        },
        [applySelection, moveSelection, normalizedBlock, selection],
    );

    const handleTabMove = useCallback(
        (direction: 'next' | 'prev') => {
            if (!selection) return false;
            const section = selection.start.section;
            const maxCol = Math.max(0, normalizedBlock.header.cells.length - 1);
            const maxRow =
                section === 'header'
                    ? headerRowCount - 1
                    : Math.max(0, normalizedBlock.rows.length - 1);
            const currentRow = selection.start.rowIndex;
            const currentCol = selection.start.colIndex;

            if (direction === 'next') {
                if (currentCol < maxCol) {
                    return moveSelection(currentRow, currentCol + 1);
                }
                // Wrap to next row
                if (currentRow < maxRow) {
                    return moveSelection(currentRow + 1, 0);
                }
                return false;
            }

            // direction === 'prev'
            if (currentCol > 0) {
                return moveSelection(currentRow, currentCol - 1);
            }
            // Wrap to previous row
            if (currentRow > 0) {
                return moveSelection(currentRow - 1, maxCol);
            }
            return false;
        },
        [moveSelection, normalizedBlock, selection],
    );

    useTableKeyboardShortcuts({
        disabled,
        selection,
        selectionRect,
        editingCell,
        hasMultiCellSelection,
        block: normalizedBlock,
        getOriginPosition,
        handleMerge,
        handleUnmerge,
        handleArrowMove,
        handleTabMove,
        startEditingCell,
        clearSelectedCells,
        copySelection,
        selectAll,
        setSelection,
    });

    useTablePaste({
        tableEditorRef: tableWrapperRef,
        disabled,
        editingCell,
        selection,
        block: normalizedBlock,
        columnWidths,
        updateBlock,
    });

    const editSeedByCellKey = useMemo(() => {
        if (!editSeed) return new Map<string, { key: number; value: string }>();
        return new Map([[editSeed.cellId, { key: editSeed.key, value: editSeed.value }]]);
    }, [editSeed]);

    // Ghost-chip clamping — keep inside table bounds so they don't overflow.
    const contentWidth = tableRef.current?.scrollWidth || 0;
    const maxGhostX = contentWidth > 0 ? contentWidth - 8 : 0;
    const clampedColumnGhostX =
        dragInsertLeft === null ? null : Math.max(8, Math.min(dragInsertLeft, maxGhostX));

    const contentHeight = tableRef.current?.scrollHeight || 0;
    const maxGhostY = contentHeight > 0 ? contentHeight - 8 : 0;
    const clampedRowGhostY =
        rowInsertTop === null ? null : Math.max(8, Math.min(rowInsertTop, maxGhostY));

    // Edge-pill positions — same offset* coordinate space as the drag
    // indicators (relative to the overlay layer, zoom-safe). The last known
    // offsets are kept in refs so the pill (the drag source) stays mounted
    // for the whole drag even if the hovered element gets disconnected.
    const lastRowPillTopRef = useRef(0);
    const lastColPillLeftRef = useRef(0);

    const hoveredRowPillTop =
        hoverTarget?.kind === 'row' && hoverTarget.element.isConnected && tableRef.current
            ? tableRef.current.offsetTop +
              hoverTarget.element.offsetTop +
              hoverTarget.element.offsetHeight / 2
            : null;
    if (hoveredRowPillTop !== null) lastRowPillTopRef.current = hoveredRowPillTop;
    const rowPillTop = hoveredRowPillTop ?? (pillDrag === 'row' ? lastRowPillTopRef.current : null);

    const hoveredColPillLeft =
        hoverTarget?.kind === 'column' && hoverTarget.element.isConnected && tableRef.current
            ? tableRef.current.offsetLeft +
              hoverTarget.element.offsetLeft +
              hoverTarget.element.offsetWidth / 2 -
              (scrollContainerRef.current?.scrollLeft ?? 0)
            : null;
    if (hoveredColPillLeft !== null) lastColPillLeftRef.current = hoveredColPillLeft;
    const colPillLeft =
        hoveredColPillLeft ?? (pillDrag === 'column' ? lastColPillLeftRef.current : null);

    const isCalculatableColumn = useCallback(
        (colIndex: number) => {
            const label = headerLabels[colIndex] || '';
            const metricId = findMetricId(label);
            return !!metricId && metricId !== 'note';
        },
        [headerLabels],
    );

    const openCalculator = (rowIndex: number) => {
        const row = normalizedBlock.rows[rowIndex];
        if (!row) return;

        const cellValueById = new Map<string, string>();
        normalizedBlock.rows.forEach((currentRow) => {
            currentRow.cells.forEach((cell) => {
                cellValueById.set(cell.id, cell.value || '');
            });
        });

        const getCellValue = (cell: TableCell) => {
            if (cell.mergedInto) {
                return cellValueById.get(cell.mergedInto) || '';
            }
            return cell.value || '';
        };

        const rowValues: Record<string, string> = {};
        row.cells.forEach((cell, colIndex) => {
            const headerLabel = headerLabels[colIndex] || '';
            rowValues[headerLabel] = getCellValue(cell);
        });

        // Keeps 'teste'/'subteste' alongside 'instrumento': the copy now says instrumento,
        // but tables pasted or imported from older laudos still carry the old headers.
        const nameHeaders = [
            'instrumento',
            'teste',
            'subteste',
            'nome',
            'item',
            'indicador',
            'escala',
        ];

        let nameColIndex = headerLabels.findIndex((label) =>
            nameHeaders.some((nameHeader) => label.toLowerCase().trim() === nameHeader),
        );

        if (nameColIndex === -1 && headerLabels.length > 0) {
            nameColIndex = 0;
        }

        const testName = nameColIndex !== -1 ? getCellValue(row.cells[nameColIndex]) : '';

        const availableColumns = headerLabels.map((label, index) => ({
            id: index.toString(),
            name: label,
        }));

        const modalRef = createRef<TestStatisticsModalHandle>();

        const applyUpdates = (updates: Record<string, string>) => {
            if (Object.keys(updates).length === 0) return;
            const next = cloneTableBlock(normalizedBlock, columnWidths);

            Object.entries(updates).forEach(([colIdx, value]) => {
                const colIndex = parseInt(colIdx, 10);
                const targetCell = next.rows[rowIndex]?.cells[colIndex];
                if (!targetCell) return;

                if (targetCell.mergedInto) {
                    const originPos = getOriginPosition(targetCell.mergedInto);
                    if (originPos) {
                        const originCell = getCellAt(
                            next,
                            originPos.section,
                            originPos.rowIndex,
                            originPos.colIndex,
                        );
                        if (originCell) originCell.value = value;
                    }
                } else {
                    targetCell.value = value;
                }
            });

            updateBlock(next);
        };

        openModal({
            variant: 'info',
            title: 'Calcular escores',
            description: testName || undefined,
            size: 'lg',
            footer: {
                closeText: 'Cancelar',
                acceptText: 'Aplicar resultados na tabela',
                onAccept: (close) => {
                    modalRef.current?.apply();
                    close();
                },
            },
            content: (
                <TestStatisticsModal
                    ref={modalRef}
                    initialData={rowValues}
                    availableColumns={availableColumns}
                    onClose={closeModal}
                    onApply={applyUpdates}
                />
            ),
        });
    };

    // What the color strip marks as applied. 'mixed' when the selection holds
    // more than one color, so neither a color nor "Cor padrão" is shown as the
    // current one.
    const selectionColor = useMemo((): TableCellColor | 'mixed' | undefined => {
        if (!selection) return undefined;

        const rect = getSelectionRectGlobal(selection, headerRowCount);
        let common: TableCellColor | undefined;
        let isFirst = true;

        for (let globalRow = rect.rowStart; globalRow <= rect.rowEnd; globalRow += 1) {
            const { section, rowIndex } = globalRowToPosition(globalRow, headerRowCount);
            for (let col = rect.colStart; col <= rect.colEnd; col += 1) {
                const cell = getCellAt(normalizedBlock, section, rowIndex, col);
                if (!cell || cell.mergedInto) continue;
                if (isFirst) {
                    common = cell.color;
                    isFirst = false;
                } else if (cell.color !== common) {
                    return 'mixed';
                }
            }
        }

        return common;
    }, [normalizedBlock, selection]);

    const canMerge =
        !!selection && hasMultiCellSelection && selection.start.section === selection.end.section;
    const canUnmerge = !!selection && selectionHasMerge();
    // No corpo, sempre. No cabeçalho, inserir só acima da folha e excluir só uma
    // faixa — a folha é a âncora de que gráficos e classificação dependem.
    const canInsertRow =
        !!selection &&
        (selection.start.section === 'body' ||
            (!!selectionRect && canInsertHeaderRowAt(normalizedBlock, selectionRect.rowStart)));
    const canDeleteRow =
        !!selection &&
        !!selectionRect &&
        (selection.start.section === 'body'
            ? normalizedBlock.rows.length > 0
            : canDeleteHeaderRowAt(normalizedBlock, selectionRect.rowStart));
    const canInsertColumn = !!selection && normalizedBlock.header.cells.length > 0;
    const canDeleteColumn =
        canInsertColumn && normalizedBlock.header.cells.length > 1 && !!selectionRect;

    useEffect(() => {
        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target as Node | null;
            if (!tableWrapperRef.current || !target) return;
            if (event.button === 2 || event.ctrlKey) return;
            if (isContextMenuOpen) return;
            if (!tableWrapperRef.current.contains(target)) {
                commitCurrentEdit();
                setEditingCell(null);
                setEditSeed(null);
                setSelection(null);
            }
        };

        document.addEventListener('pointerdown', handlePointerDown);
        return () => document.removeEventListener('pointerdown', handlePointerDown);
    }, [commitCurrentEdit, isContextMenuOpen, setSelection]);

    return (
        <div
            className="space-y-3"
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            tabIndex={0}
            data-table-editor
        >
            <TableContextMenu
                disabled={disabled}
                canMerge={canMerge}
                canUnmerge={canUnmerge}
                canInsertRow={canInsertRow}
                canDeleteRow={canDeleteRow}
                canInsertColumn={canInsertColumn}
                canDeleteColumn={canDeleteColumn}
                onOpenChange={setIsContextMenuOpen}
                onMerge={handleMerge}
                onUnmerge={handleUnmerge}
                onInsertRowAbove={handleInsertRowAbove}
                onInsertRowBelow={handleInsertRowBelow}
                onDeleteRow={handleDeleteRows}
                onInsertColumnLeft={handleInsertColumnLeft}
                onInsertColumnRight={handleInsertColumnRight}
                onDeleteColumn={handleDeleteColumns}
                onAddChart={onAddChart}
                currentColor={selectionColor}
                onColorChange={handleSetColor}
            >
                <div
                    ref={tableWrapperRef}
                    // Radix's trigger preventDefaults but never stops
                    // propagation, so the page-level menu wrapping
                    // .report-paper would otherwise open stacked on top of the
                    // table menu. Radix's own handler still runs — stopPropagation
                    // does not set defaultPrevented.
                    onContextMenu={(event) => event.stopPropagation()}
                    {...tableHover.dataProps}
                    onMouseEnter={tableHover.hoverProps.onMouseEnter}
                    onMouseLeave={() => {
                        tableHover.hoverProps.onMouseLeave();
                        if (!pillDragRef.current) setHoverTarget(null);
                    }}
                    className={cn(
                        'group/tbl relative rounded-xl',
                        selection ? 'ring-1 ring-blue-200/60' : 'ring-1 ring-transparent',
                    )}
                >
                    <div className="relative">
                        <TableRenderer
                            block={{ ...normalizedBlock, columnWidths }}
                            selection={selection}
                            editingCell={editingCell}
                            disabled={disabled}
                            headerColor={headerBackground}
                            headerTextColor={headerTextColor}
                            textAlignment={textAlignment}
                            classificationColIndex={classificationColIndex}
                            onCellMouseDown={handleCellMouseDown}
                            onCellMouseEnter={handleCellMouseEnter}
                            onCellClick={handleCellClick}
                            onCellContextMenu={handleCellContextMenu}
                            onStartEdit={handleStartEdit}
                            onCommit={handleCommit}
                            onCancelEdit={handleCancel}
                            onDraftChange={handleDraftChange}
                            onOpenCalculator={(rowIndex, _colIndex) => openCalculator(rowIndex)}
                            isCalculatableColumn={isCalculatableColumn}
                            onResizeStart={handleResizeStart}
                            onScroll={() => {
                                if (!pillDragRef.current) setHoverTarget(null);
                            }}
                            editSeedByCellKey={editSeedByCellKey}
                            editCursorPosition={editCursorPosition}
                            scrollContainerRef={scrollContainerRef}
                            tableInnerRef={tableInnerRef}
                            tableRef={tableRef}
                            measurementId={measurementId}
                        />

                        {/* Drag indicators rendered outside the scroll container
                            so ghost chips are never clipped by overflow-auto. */}
                        <DragIndicators
                            direction="vertical"
                            insertOffset={dragInsertLeft}
                            ghostOffset={clampedColumnGhostX}
                            ghostLabel={dragGhostLabel}
                            transitionMs={dragInsertTransitionMs}
                        />
                        <DragIndicators
                            direction="horizontal"
                            insertOffset={rowInsertTop}
                            ghostOffset={clampedRowGhostY}
                            ghostLabel={rowGhostLabel}
                            transitionMs={rowInsertTransitionMs}
                        />

                        {!disabled && tableHover.isActive && rowPillTop !== null && (
                            <TableEdgePill
                                orientation="row"
                                offset={rowPillTop}
                                dragging={pillDrag === 'row'}
                                showGrip={normalizedBlock.rows.length > 1}
                                onDragStart={handleRowPillDragStart}
                                onDragEnd={handleRowPillDragEnd}
                                onAdd={handlePillAddRow}
                                addTitle="Adicionar linha abaixo"
                                onSelect={() => {
                                    if (hoverTarget?.kind === 'row') {
                                        handleSelectRow(hoverTarget.index);
                                    }
                                }}
                                onContextSelect={() => {
                                    if (hoverTarget?.kind === 'row') {
                                        handleSelectRow(hoverTarget.index);
                                    }
                                }}
                            />
                        )}
                        {!disabled && tableHover.isActive && colPillLeft !== null && (
                            <TableEdgePill
                                orientation="column"
                                offset={colPillLeft}
                                dragging={pillDrag === 'column'}
                                showGrip={columnWidths.length > 1}
                                onDragStart={handleColumnPillDragStart}
                                onDragEnd={handleColumnPillDragEnd}
                                onAdd={handlePillAddColumn}
                                addTitle="Adicionar coluna à direita"
                                onSelect={() => {
                                    if (hoverTarget?.kind === 'column') {
                                        handleSelectColumn(hoverTarget.index);
                                    }
                                }}
                                onContextSelect={() => {
                                    if (hoverTarget?.kind === 'column') {
                                        handleSelectColumn(hoverTarget.index);
                                    }
                                }}
                            />
                        )}
                    </div>
                </div>
            </TableContextMenu>
        </div>
    );
}
